import React, { useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { useSeo } from "../../lib/seo";
import { LayoutDashboard, Package, Car, ClipboardList, Upload, LogOut, Users, Tag, MessageSquare, Truck, Rss } from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, perm: "view_dashboard" },
  { to: "/admin/products", label: "Products", icon: Package, perm: "manage_products" },
  { to: "/admin/coupons", label: "Coupons", icon: Tag, perm: "manage_products" },
  { to: "/admin/inquiries", label: "Inquiries", icon: MessageSquare, perm: "view_dashboard" },
  { to: "/admin/vehicles", label: "Vehicles", icon: Car, perm: "manage_vehicles" },
  { to: "/admin/orders", label: "Orders", icon: ClipboardList, perm: "view_orders" },
  { to: "/admin/shipping", label: "Shipping", icon: Truck, perm: "manage_products" },
  { to: "/admin/merchant-feed", label: "Merchant Feed", icon: Rss, perm: "manage_products" },
  { to: "/admin/bulk-upload", label: "Bulk Upload", icon: Upload, perm: "manage_csv_import" },
  { to: "/admin/users", label: "Users", icon: Users, perm: "manage_users" },
];

export default function AdminLayout({ children, title, actions }) {
  const { admin, checking, logout, hasPerm } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  useSeo({ title: `${title || "Admin"} · Lecis`, description: "Lecis admin", robots: "noindex,nofollow" });

  // Force-reset guard: any logged-in admin with must_reset_password=true is
  // bounced to /admin/change-password (except the change-password page itself).
  useEffect(() => {
    if (admin && admin.must_reset_password && location.pathname !== "/admin/change-password") {
      navigate("/admin/change-password", { replace: true });
    }
  }, [admin, location.pathname, navigate]);

  if (checking) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Loading…</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 flex">
      <aside className="w-60 bg-white border-r border-zinc-200 flex flex-col" data-testid="admin-sidebar">
        <div className="px-5 py-5 border-b border-zinc-200">
          <Link to="/admin" className="font-display text-xl font-bold tracking-tighter">
            LECIS<span className="text-red-600">.</span>
          </Link>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-1">Admin</div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {NAV.filter((n) => !n.perm || hasPerm(n.perm)).map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`admin-nav-${label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2 text-sm rounded ${isActive ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-100"}`}
            >
              <Icon size={16} />{label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-zinc-200 px-3 py-3">
          <button
            onClick={async () => { await logout(); navigate("/admin/login"); }}
            data-testid="admin-logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-100 rounded"
          >
            <LogOut size={16} /> Sign out
          </button>
          <div className="px-3 py-2 text-[11px] text-zinc-500 truncate">{admin?.email}</div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 border-b border-zinc-200 bg-white px-8 flex items-center justify-between">
          <h1 className="font-display text-xl tracking-tight" data-testid="admin-page-title">{title}</h1>
          {actions}
        </header>
        <div className="flex-1 px-8 py-8">{children}</div>
      </main>
    </div>
  );
}
